import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { groupService, Group } from '../services/groupService';
import { useNavigate } from 'react-router-dom';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

export const LicenseAssignment: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const navigate = useNavigate();

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await groupService.getGroups({ top: 999 });
      const allGroups = getApiCollection<Group>(response, 'loading license groups');

      // Filter groups that start with "Lisens -"
      const licenseGroups = allGroups.filter((group: any) => {
        const displayName = getProp(group, 'displayName') || '';
        return displayName.startsWith('Lisens -');
      });

      setGroups(licenseGroups);
    } catch (err: any) {
      setError(err.message || 'Failed to load license groups');
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;

    const search = searchTerm.toLowerCase();
    return groups.filter((group: any) => {
      const displayName = getProp(group, 'displayName')?.toLowerCase() || '';
      const description = getProp(group, 'description')?.toLowerCase() || '';
      return displayName.includes(search) || description.includes(search);
    });
  }, [groups, searchTerm]);

  const paginatedGroups = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredGroups.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredGroups, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (groupId: string) => {
    navigate(`/groups/${groupId}`);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Licence Assignment
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={styles.filterSection}>
        <TextField
          placeholder="Search licence groups..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <Typography variant="body2" color="text.secondary">
          {filteredGroups.length} licence group{filteredGroups.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Group Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Description</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Type</TableCell>
              <TableCell sx={styles.tableHeaderCell} align="right">
                Members
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.map((group: any) => {
              const id = getProp(group, 'id');
              const displayName = getProp(group, 'displayName');
              const description = getProp(group, 'description');
              const groupTypes = getProp(group, 'groupTypes') || [];
              const isMicrosoft365 = groupTypes.includes('Unified');
              const securityEnabled = getProp(group, 'securityEnabled');

              return (
                <TableRow key={id} hover sx={styles.tableRow} onClick={() => handleRowClick(id)}>
                  <TableCell>{displayName}</TableCell>
                  <TableCell>{description || '-'}</TableCell>
                  <TableCell>
                    {isMicrosoft365 ? (
                      <Chip label="Microsoft 365" size="small" color="primary" />
                    ) : securityEnabled ? (
                      <Chip label="Security" size="small" color="secondary" />
                    ) : (
                      <Chip label="Distribution" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
              );
            })}
            {paginatedGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No license groups matched the current data set. If you expected results, verify
                    group naming and API permissions.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};
